import React, { createContext, useContext, useEffect, useState } from 'react';
import nl from '../locales/nl.json';
import en from '../locales/en.json';
import fr from '../locales/fr.json';

const LanguageContext = createContext();

const translations = { nl, en, fr };
const languageNames = {
  nl: 'Nederlands',
  en: 'English',
  fr: 'Français'
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLocale, setCurrentLocale] = useState(() => {
    return localStorage.getItem('preferredLocale') || 'nl';
  });

  const changeLanguage = (locale) => {
    if (translations[locale]) {
      setCurrentLocale(locale);
      localStorage.setItem('preferredLocale', locale);
    }
  };

  const t = (key, variables = {}) => {
    const keys = key.split('.');
    let value = translations[currentLocale];

    for (const k of keys) {
      value = value?.[k];
      if (!value) return key;
    }

    if (typeof value === 'string') {
      return value.replace(/\{\{(\w+)\}\}/g, (_, v) => (variables[v] !== undefined ? variables[v] : _));
    }
    return value;
  };

  const getLanguageName = (locale) => languageNames[locale] || locale.toUpperCase();
  const [version, setVersion] = useState('');

  useEffect(() => {
    let isMounted = true;

    // Determine backend URL based on environment
    const getApiBase = () => {
      if (process.env.REACT_APP_BACKEND_URL) {
        return process.env.REACT_APP_BACKEND_URL;
      }
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return 'http://localhost:3001';
      }
      if (
        window.location.hostname.startsWith('192.168.') ||
        window.location.hostname.startsWith('10.')
      ) {
        return `http://${window.location.hostname}:3001`;
      }
      return '';
    };

    const apiBase = getApiBase();
    const versionUrl = apiBase ? `${apiBase}/api/version` : '/api/version';

    fetch(versionUrl)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!isMounted) return;
        setVersion(data?.version || '');
      })
      .catch(() => {
        if (isMounted) setVersion('');
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const value = {
    currentLocale,
    availableLocales: Object.keys(translations),
    t,
    changeLanguage,
    getLanguageName,
    version,
    isLoading: false
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
